import { imageFieldsFrag } from './imageFieldsFrag'
import { type SanitizedResult } from '@amzn/amp-gql'
import { graphql } from './graphql'
import { playableEntityFrag } from './playableEntityFrag'
import { playParamsFrag } from './playParamsFrag'
import { trackLikesContextFrag } from './trackLikesContextFrag'

// Everything a track row needs EXCEPT its contributing-artist selection, which
// differs by surface: a dense list caps hard to protect the gateway's Artist
// entity budget, while the single-track detail page pulls the full set. Both
// compose this core so the rest of the shape stays identical. See
// {@link trackFieldsFrag} (dense) and trackDetailFieldsFrag (detail).
// language=GraphQL
export const trackCommonFieldsFrag = graphql(
    `
        fragment trackCommonFields on Track @_unmask {
            ...playableEntity
            id
            mediaType
            title
            shortTitle
            ...trackLikesContext
            purchaseContext @include(if: $isAuthenticated) {
                id
                isPurchased
            }
            parentalSettings {
                hasExplicitLanguage
                isKidFriendly
            }
            audioQualities
            hasLyrics
            album {
                id
                title
                url
                shortTitle
                playParams {
                    ...playParams
                }
            }
            duration
            images {
                ...imageFields
            }
        }
    `,
    [imageFieldsFrag, playableEntityFrag, playParamsFrag, trackLikesContextFrag]
)

// The dense-list track fragment. Artist DISPLAY comes from the precomputed
// `concatenatedName` scalar (zero Artist entities resolved); `first: 1` keeps
// only the primary artist's id for the overflow "go to artist" action. NO
// `totalCount` — counting the connection makes the gateway resolve EVERY
// contributing artist, which blows its 1000-Artist entity/query limit across a
// 50-row page of a feature-heavy (e.g. classical) playlist.
// language=GraphQL
export const trackFieldsFrag = graphql(
    `
        fragment trackFields on Track @_unmask {
            ...trackCommonFields
            contributingArtists(first: 1) {
                concatenatedName
                edges {
                    node {
                        id
                        name
                    }
                }
            }
        }
    `,
    [trackCommonFieldsFrag]
)

export type TrackFields = SanitizedResult<typeof trackFieldsFrag>
